import { useEffect, useState } from "react";
import { getPreviewScale } from "../functions/previewScale";
import { anchorFor, getViewScale, ScaleAnchor } from "../functions/keepScale";

export interface KeepScaleView {
  // CSS scale for the canvas image (1 = no transform).
  previewScale: number;
  // True while the true-scale view is active (the drag's px/mm is kept).
  trueScale: boolean;
  // A drag has anchored a true scale on this logo, so the view can be toggled.
  canToggle: boolean;
  // Corner drag started: keep whatever scale it runs at after the release.
  onDragStart: () => void;
  // Switch between the true-scale view and the fit view.
  toggle: () => void;
}

/**
 * Flag REACT_APP_LOGO_DRAG_RESIZE_KEEP_SCALE: view state of the logo editor
 * canvas. Opening is the fit view (today's scale). A corner drag anchors the
 * px/mm it runs at - the logo's saved size when the drag starts, which is
 * what the fit box paints - and switches to the true-scale view, so the
 * release and the resize response leave the logo at the dragged size on
 * screen instead of snapping back to the fit box. "Fit to view" toggles back
 * to the fit view and the anchor is kept, so the true scale can be restored.
 * A new logo under the tool starts over in the fit view. Pure view state:
 * nothing here touches the form or the API. Disabled = the fit view only,
 * exactly as without the flag.
 */
export function useKeepScaleView(
  enabled: boolean,
  form: { id?: number; widthMm: number; heightMm: number },
  logo: { id?: number; widthMm?: number; heightMm?: number },
  maxScale: number
): KeepScaleView {
  const [anchor, setAnchor] = useState<ScaleAnchor | null>(null);
  const [trueScale, setTrueScale] = useState(false);

  useEffect(() => {
    setAnchor(null);
    setTrueScale(false);
  }, [logo.id]);

  const anchorIsCurrent =
    !!anchor &&
    (anchor.logoId === undefined ||
      logo.id === undefined ||
      anchor.logoId === logo.id);
  const active = enabled && trueScale && anchorIsCurrent;

  return {
    previewScale: active
      ? getViewScale(form, logo, anchor, maxScale)
      : getPreviewScale(form, logo, maxScale),
    trueScale: active,
    canToggle: enabled && anchorIsCurrent,
    onDragStart: () => {
      if (!enabled) return;
      // In the fit view the drag runs against the saved size; re-anchor there.
      // In the true-scale view the existing anchor is what the drag runs at.
      if (!active) {
        const next = anchorFor(logo);
        if (!next) return;
        setAnchor(next);
      }
      setTrueScale(true);
    },
    toggle: () => {
      if (!enabled || !anchorIsCurrent) return;
      setTrueScale((v) => !v);
    },
  };
}
